import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { useState } from "react";
import { canUseAction } from "../actionRules";
import { ACTIONS, ROLES } from "../gameData";
import type { ActionType, Player } from "../types";

interface Props {
  me: Player;
  disabled: boolean;
  targeting: ActionType | null;
  onTarget: (type: ActionType) => void;
  onCancelTarget: () => void;
  onDeclare: (type: ActionType) => void;
}

const DIRECT_ACTIONS: ActionType[] = ["allowance", "patronage", "banish"];
const BLUFF_ACTIONS: ActionType[] = ["levy", "silence", "seize", "exchange"];
const BLUFF_ANGLE_SETS: Record<number, number[]> = {
  0: [],
  1: [-90],
  2: [-138, -42],
  3: [-150, -90, -30],
  4: [-156, -112, -68, -24]
};

/**
 * The dock is now three things: the direct actions everybody can take, the
 * bluff wheel (roles you do NOT hold), and — mounted on the cards themselves,
 * see MyHand — the abilities you actually own. Nothing stacks above the quick
 * actions any more.
 */
export function ActionDock({ me, disabled, targeting, onTarget, onCancelTarget, onDeclare }: Props) {
  const [bluffOpen, setBluffOpen] = useState(false);
  const mandatory = me.coins >= 10;
  const liveRoles = new Set(
    me.influence.flatMap((card) => (!card.revealed && card.role ? [card.role] : []))
  );
  const bluffActions = BLUFF_ACTIONS.filter((type) => {
    const role = ACTIONS[type].role;
    return !role || !liveRoles.has(role);
  });
  const bluffAngles = BLUFF_ANGLE_SETS[bluffActions.length] ?? BLUFF_ANGLE_SETS[4];

  const canUse = (type: ActionType) => canUseAction(me, type);

  const chooseAction = (type: ActionType) => {
    if (disabled || !canUse(type)) return;
    setBluffOpen(false);
    if (ACTIONS[type].target) {
      if (targeting === type) onCancelTarget();
      else onTarget(type);
    } else {
      onDeclare(type);
    }
  };

  return (
    <section
      className={[
        "action-dock",
        disabled ? "action-dock--disabled" : "",
        targeting ? "action-dock--targeting" : "",
        bluffOpen ? "action-dock--bluff-open" : ""
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="quick-actions" aria-label="Direct actions">
        {DIRECT_ACTIONS.map((type) => {
          const action = ACTIONS[type];
          const Icon = action.icon;
          return (
            <motion.button
              key={type}
              type="button"
              className={`quick-action quick-action--${type}`}
              disabled={disabled || !canUse(type)}
              onClick={() => chooseAction(type)}
              whileTap={{ scale: 0.92, rotate: type === "banish" ? 2 : -2 }}
            >
              <Icon size={18} strokeWidth={2} />
              <span>{action.name}</span>
              <b>{action.short}</b>
            </motion.button>
          );
        })}
      </div>

      <div className="bluff-control">
        <AnimatePresence>
          {bluffOpen && !targeting && (
            <motion.div
              className="bluff-wheel"
              initial={{ opacity: 0, scale: 0.65 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.72 }}
              transition={{ type: "spring", stiffness: 420, damping: 26 }}
            >
              {bluffActions.map((type, index) => {
                const action = ACTIONS[type];
                const role = action.role ? ROLES[action.role] : null;
                const Icon = action.icon;
                const angle = bluffAngles[index];
                return (
                  <button
                    className="bluff-wedge"
                    type="button"
                    key={type}
                    disabled={disabled || !canUse(type)}
                    onClick={() => chooseAction(type)}
                    style={
                      {
                        "--angle": `${angle}deg`,
                        "--counter-angle": `${-angle}deg`,
                        "--role-color": role?.color ?? "var(--yellow)"
                      } as React.CSSProperties
                    }
                    aria-label={`${action.name} as ${role?.name}`}
                  >
                    <span className="bluff-wedge__content">
                      <Icon size={18} strokeWidth={2.2} />
                      <b>{action.name}</b>
                      <small>{role?.name}</small>
                    </span>
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          className={targeting ? "bluff-trigger bluff-trigger--cancel" : "bluff-trigger"}
          type="button"
          disabled={disabled || (mandatory && !targeting)}
          aria-expanded={bluffOpen}
          aria-label={
            targeting
              ? "Cancel target selection"
              : bluffOpen
                ? "Close bluff menu"
                : "Open bluff menu"
          }
          onClick={() => {
            if (targeting) onCancelTarget();
            else setBluffOpen((current) => !current);
          }}
          whileTap={{ scale: 0.92 }}
        >
          {targeting ? <X size={23} /> : <Sparkles size={22} />}
          <span>{targeting ? "Cancel" : "Tap to bluff"}</span>
        </motion.button>
      </div>

      {(targeting || mandatory) && (
        <div className="action-dock__status" aria-live="polite">
          {targeting
            ? `${ACTIONS[targeting].name}: tap a rival above`
            : "10 Coins — Coup is mandatory"}
        </div>
      )}
    </section>
  );
}
